import { useEffect, useRef, useState } from 'react';
import { createCustomer, type Customer, type TerminalIdentity } from '@makila/pos-core';
import { UserPlus, X } from 'lucide-react';
import type { IdbTerminalStore } from '../lib/idb-store';

interface Row { id: string; name: string; nuit: string | null; active: boolean }

/** Escolher o cliente da venda (por nome ou NUIT) ou criá-lo já, mesmo sem rede. */
export function CustomerDialog({ store, who, onPick, onClose }: { store: IdbTerminalStore; who: TerminalIdentity; onPick: (c: Customer) => void; onClose: () => void }) {
  const dlg = useRef<HTMLDialogElement>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ name: '', nuit: '', phone: '' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dlg.current?.showModal();
    void store.catalog('customer').then((i) => setRows(i.map((x) => x.data as unknown as Row).filter((r) => r.active).sort((a, b) => a.name.localeCompare(b.name, 'pt'))));
  }, [store]);

  const s = q.trim().toLowerCase();
  const digits = q.replace(/\D/g, '');
  const list = rows.filter((r) => !s || r.name.toLowerCase().includes(s) || (digits.length >= 3 && r.nuit?.includes(digits))).slice(0, 30);

  return (
    <dialog ref={dlg} onCancel={(e) => { e.preventDefault(); onClose(); }} className="m-auto w-[min(560px,96vw)] rounded-2xl bg-[var(--surface)] p-0 text-[var(--text)]">
      <div className="flex items-center justify-between border-b border-base px-5 py-3">
        <h2 className="text-xl font-bold">{creating ? 'Novo cliente' : 'Cliente da venda'}</h2>
        <button aria-label="Fechar" onClick={onClose} className="grid h-12 w-12 place-items-center rounded-lg"><X /></button>
      </div>
      <div className="max-h-[70vh] overflow-y-auto p-5">
        {!creating && (
          <>
            <input autoFocus aria-label="Pesquisar cliente" placeholder="Nome ou NUIT" value={q} onChange={(e) => setQ(e.target.value)}
              className="h-12 w-full rounded-xl border border-base bg-surface-2 px-4" />
            <ul className="mt-3 divide-y divide-[var(--border)]">
              {list.map((r) => (
                <li key={r.id}>
                  <button onClick={() => onPick({ id: r.id, name: r.name, nuit: r.nuit })} className="flex h-14 w-full items-center justify-between px-2 text-left hover:bg-surface-2">
                    <span className="font-medium">{r.name}</span><span className="font-mono text-sm text-muted">{r.nuit ?? 'sem NUIT'}</span>
                  </button>
                </li>
              ))}
              {!list.length && <li className="p-3 text-muted">Nenhum cliente encontrado.</li>}
            </ul>
            <button onClick={() => { setCreating(true); setF({ name: /\d/.test(q) ? '' : q, nuit: /\d/.test(q) ? digits : '', phone: '' }); }} className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-base font-semibold">
              <UserPlus size={18} /> Novo cliente
            </button>
          </>
        )}
        {creating && (
          <form onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            try { onPick(await createCustomer(store, who, { name: f.name, nuit: f.nuit || null, phone: f.phone || null })); } catch (err) { setError((err as Error).message); }
          }}>
            <label className="block text-sm font-medium">Nome<input required autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 h-12 w-full rounded-xl border border-base bg-surface-2 px-4" /></label>
            <label className="mt-3 block text-sm font-medium">NUIT (opcional)<input inputMode="numeric" value={f.nuit} onChange={(e) => setF({ ...f, nuit: e.target.value })} placeholder="9 algarismos" className="mt-1 h-12 w-full rounded-xl border border-base bg-surface-2 px-4 font-mono" /></label>
            <label className="mt-3 block text-sm font-medium">Telefone (opcional)<input inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1 h-12 w-full rounded-xl border border-base bg-surface-2 px-4" /></label>
            {error && <p role="alert" className="mt-3 rounded-lg bg-brand-50 p-3 text-sm text-brand-800">{error}</p>}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setCreating(false)} className="h-12 rounded-xl border border-base font-medium">Voltar</button>
              <button className="h-12 rounded-xl bg-brand-700 font-bold text-white">Criar e usar</button>
            </div>
          </form>
        )}
      </div>
    </dialog>
  );
}
